import { useEffect, useRef } from 'react';
import { debounce } from '@saviynt/common';
import { useQuery } from '@tanstack/react-query';

import {
  getListRowApiFilterAndSearch,
  getRolesListApi,
} from '../utilities/api/getDataFromApi';
import { LANDING_PAGE_INPUT_DEBOUNCE_DELAY } from '../utilities/debounce';

const getListRowQueryConfigs = (
  checkedPlatforms,
  checkedAssets,
  checkedRoles,
  searchBarValue,
  isUserOnBulkSelectMode,
  isJitForApps
) => ({
  queryKey: [
    'listRowData',
    checkedPlatforms,
    checkedAssets,
    checkedRoles,
    searchBarValue,
    isUserOnBulkSelectMode,
  ],
  queryFn: () => {
    if (isUserOnBulkSelectMode) {
      return getRolesListApi(
        checkedPlatforms,
        checkedAssets,
        checkedRoles,
        searchBarValue
      ).then((response) => {
        if (response.ok !== undefined && response.ok === false) {
          throw new Error('getRolesListApi API call failed.');
        }

        return response;
      });
    }

    return getListRowApiFilterAndSearch(
      checkedPlatforms,
      checkedAssets,
      checkedRoles,
      searchBarValue,
      isJitForApps
    ).then((response) => {
      if (response.ok !== undefined && response.ok === false) {
        throw new Error('getListRowApiFilterAndSearch API call failed.');
      }

      return response;
    });
  },
  refetchOnWindowFocus: true,
  enabled: !searchBarValue || searchBarValue.length === 0,
  retry: 1, // for failed requests
});

/**
 * Custom hook for fetching list row data.
 *
 * @param {string} searchBarValue - value of the search bar.
 * @param {Array} checkedPlatforms - array of selected platforms.
 * @param {Array} checkedAssets - array of selected assets.
 * @param {Array} checkedRoles - array of selected roles.
 * @param {function} setListRowData - function/setter to set the list row data.
 * @returns {Object} - object containing the fetched list row data and various loading/error states.
 */
function useFetchListRowData(
  searchBarValue,
  checkedPlatforms,
  checkedAssets,
  checkedRoles,
  setListRowData,
  isUserOnBulkSelectMode,
  isJitForApps
) {
  const {
    data: fetchedListRowData,
    isLoading: isListRowApiLoading,
    isFetching: isListRowApiFetching,
    error: listRowApiError,
    isSuccess: isListRowApiSuccess,
    isError: isListRowApiError,
    refetch,
  } = useQuery(
    getListRowQueryConfigs(
      checkedPlatforms,
      checkedAssets,
      checkedRoles,
      searchBarValue,
      isUserOnBulkSelectMode,
      isJitForApps
    )
  );

  const debouncedRefetch = useRef(
    debounce(() => {
      refetch();
    }, LANDING_PAGE_INPUT_DEBOUNCE_DELAY)
  ).current;

  useEffect(() => {
    // refetch with debounce when search filter is applied
    if (searchBarValue !== '') {
      debouncedRefetch();
    }

    return () => {
      debouncedRefetch?.cancel();
    };
  }, [
    searchBarValue,
    checkedPlatforms,
    checkedAssets,
    checkedRoles,
    debouncedRefetch,
  ]);

  useEffect(() => {
    if (isListRowApiSuccess) {
      setListRowData(fetchedListRowData);
    }
  }, [isListRowApiSuccess, fetchedListRowData]);

  return {
    fetchedListRowData,
    isListRowApiLoading,
    isListRowApiFetching,
    listRowApiError,
    isListRowApiSuccess,
    isListRowApiError,
  };
}

export default useFetchListRowData;
